import { ChevronLeft, ChevronRight } from '@aktiia/design-system';
import cx from 'classnames';
import { isToday } from 'date-fns';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { RangeTypes } from '../../../../../constants';
import { Direction } from '../../../../../enums';
import {
  useChangeDateRange,
  useChangeRangeType,
  useDateRange,
  useDateRangeText,
  usePhysiologicalData,
} from '../../../../../features/physiological-data';
import { Button } from '../../../../common';
import { IStatisticsHeaderBarProps } from './types';

export const StatisticsHeaderBar = ({ wrapperRef }: IStatisticsHeaderBarProps) => {
  const { t } = useTranslation();
  const { physiologicalData, rangeType } = usePhysiologicalData();
  const { dateTo } = useDateRange();
  const changeRangeType = useChangeRangeType();
  const changeDateRange = useChangeDateRange();

  const [isHeaderSticky, setIsHeaderSticky] = useState(false);
  const checkHeaderStickiness = useCallback(() => {
    if (!wrapperRef.current) return;
    const { y } = wrapperRef.current.getBoundingClientRect();
    setIsHeaderSticky(y < 60);
  }, [wrapperRef]);

  useEffect(() => {
    window.addEventListener('scroll', checkHeaderStickiness, { passive: true });
    return () => window.removeEventListener('scroll', checkHeaderStickiness);
  }, [checkHeaderStickiness]);

  const avgText = useMemo(() => {
    if (physiologicalData && physiologicalData.avg && Object.values(physiologicalData.avg).length) {
      const { sys, dia } = physiologicalData.avg;
      return t('patient.label.bpAverage.value', { sys: Math.round(sys), dia: Math.round(dia) });
    } else {
      return t('patient.label.bpAverage.unknown');
    }
  }, [physiologicalData]);

  const dateText = useDateRangeText();

  const handleChangeDateRange = useCallback(
    (direction: Direction) => {
      changeDateRange(direction);
    },
    [changeDateRange],
  );

  return (
    <>
      <div
        id={isHeaderSticky ? 'stickyHeader' : ''}
        className={cx('flex items-center justify-between mb-5 pt-6', {
          'fixed top-16 z-[5] left-60 ml-6 px-6 pt-0 h-19 items-center right-6 bg-background-base-primary h-[4.75rem] shadow-high':
            isHeaderSticky,
        })}
      >
        <div className="w-1/3 flex flex-col items-start justify-center text-text-primary">
          <span className="typography-title-3">
            {t('patient.label.bpAverage.description', { count: physiologicalData?.count })}
          </span>
          <span className="typography-heading-3">{avgText}</span>
        </div>
        <div className="w-1/3 flex items-center justify-center">
          <button onClick={() => handleChangeDateRange(Direction.BACKWARD)}>
            <ChevronLeft />
          </button>
          <span className="mx-4 typography-heading-2 text-text-primary">{dateText}</span>
          <button
            className="disabled:opacity-medium"
            disabled={isToday(dateTo)}
            onClick={() => handleChangeDateRange(Direction.FORWARD)}
          >
            <ChevronRight />
          </button>
        </div>
        <div className="w-1/3 flex gap-2 justify-end items-start">
          {RangeTypes.map((key) => (
            <Button
              key={key}
              text={t(`common.dateRange.${key}.button`)}
              type="secondary"
              size="sm"
              onClick={() => changeRangeType(key)}
              selected={rangeType === key}
            />
          ))}
        </div>
      </div>

      {isHeaderSticky && <div className="h-[60px] mb-5"></div>}
    </>
  );
};
